import ExcelJS, { Row } from 'exceljs';
import { Flow } from '@thive/fddm-types';
import { useCallback, useMemo } from 'react';

import { download } from '@/modules/common/helpers/browser';
import { MAX_ORDER_PROFILE_HOURS } from '@/modules/common/constants/orderProfile';
import {
  markCellModifiable,
  writeHeaderRow,
  writeInfoSection,
} from '@/modules/common/helpers/excel';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { isAtLeast1, minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { OrderProfile } from '../types';
import { OrderProfileServiceFactory } from '../service/OrderProfileServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';

const EXCEL_SHEET_NAME = 'CurstomTransportFlows';
const EXCEL_HEADER_FLOW_ID = 'Flow id';
const EXCEL_HEADER_FLOW_NAME = 'Flow name';

export type UploadExcelResult = {
  success: boolean;
  message?: string;
};

export const useOrderProfileExcel = () => {
  const defaultFlowTransportLoadPerHour = 30;
  const { getFlows, getAreaById, getFlowStopById } = useFloorplanDesignStore.getState();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const opSvc = useMemo(
    () => OrderProfileServiceFactory.getInstance().createOrderProfileService(),
    [],
  );
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  const createExcel = useCallback(
    async (projectName: string, floorplanName: string, version: number): Promise<Blob> => {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(EXCEL_SHEET_NAME);
      const allFlows = getFlows();

      let rowIndex = writeInfoSection(sheet, projectName, floorplanName, version);
      rowIndex += 1;

      // order profile table: header
      const header: string[] = [EXCEL_HEADER_FLOW_NAME, 'From', 'To'];
      for (let i = 1; i <= MAX_ORDER_PROFILE_HOURS; ++i) {
        header.push(i.toString());
      }
      header.push(EXCEL_HEADER_FLOW_ID);

      writeHeaderRow(sheet, rowIndex, header);

      sheet.columns = header.map((h, index) => {
        const width = index < 3 ? 20 : 5;
        return { width };
      });
      ++rowIndex;

      const filteredFlows = allFlows.filter((f) => f.type === 'TRANSPORT');

      // order profile table: flows
      filteredFlows.forEach((flow) => {
        const sourceName =
          getAreaById(flow.sourceAreaOrFlowStopId)?.name ||
          getFlowStopById(flow.sourceAreaOrFlowStopId)?.name ||
          '';

        const destinationName =
          getAreaById(flow.destinationAreaOrFlowStopId)?.name ||
          getFlowStopById(flow.destinationAreaOrFlowStopId)?.name ||
          '';

        let rowValue = [flow.name, sourceName, destinationName];
        const items: string[] = [];
        for (let i = 0; i < MAX_ORDER_PROFILE_HOURS; ++i) {
          let value = flow.hourlyOrderCounts[0];
          if (i < flow.hourlyOrderCounts.length) value = flow.hourlyOrderCounts[i];
          items.push(value.toString());
        }
        rowValue = rowValue.concat(items);
        rowValue.push(flow.id);
        const row = sheet.getRow(rowIndex);
        row.values = rowValue;

        // fill background for modifiable cells
        markCellModifiable(row.getCell(1));
        for (let i = 4; i < rowValue.length; ++i) {
          markCellModifiable(row.getCell(i));
        }
        ++rowIndex;
      });

      const excelBuffer = await workbook.xlsx.writeBuffer();
      const excelBlob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      return excelBlob;
    },
    [getAreaById, getFlowStopById, getFlows],
  );

  const downloadExcel = useCallback(
    (excelBlob: Blob, layoutAlternativeName: string, versionNr: number) => {
      download(excelBlob, `CustomTransportFlows-${layoutAlternativeName}-v${versionNr}.xlsx`);
    },
    [],
  );

  const findValueCellIndex = (row: Row, value: string) => {
    for (let i = 1; i <= row.cellCount; ++i) {
      if (row.getCell(i).value === value) return i;
    }
    return null;
  };

  const uploadExcel = useCallback(
    async (buffer: ArrayBuffer, filename: string): Promise<UploadExcelResult> => {
      const workbook = new ExcelJS.Workbook();
      workbook.calcProperties.fullCalcOnLoad = true;

      try {
        await workbook.xlsx.load(buffer);
      } catch {
        return {
          success: false,
          message: 'interface:order_profile.upload_result.bad_excel',
        };
      }

      const sheet = workbook.getWorksheet(EXCEL_SHEET_NAME);
      if (!sheet) {
        return {
          success: false,
          message: 'interface:order_profile.upload_result.bad_excel',
        };
      }

      // add key back to column
      let rowIndex = 1;

      // find the table header row
      for (let i = rowIndex; i < sheet.rowCount; ++i) {
        if (sheet.getCell(`A${i}`).value === EXCEL_HEADER_FLOW_NAME) {
          rowIndex = i;
          break;
        }
      }

      // flow id index
      const flowIdCol = findValueCellIndex(sheet.getRow(rowIndex), EXCEL_HEADER_FLOW_ID);
      if (!flowIdCol)
        return {
          success: false,
          message: 'interface:order_profile.upload_result.bad_excel',
        };
      ++rowIndex;

      // update allFlows
      const oldFlows = getFlows();
      const oldFlowsIds = new Set(oldFlows.map((flow) => flow.id));
      const newFlows: Flow[] = [];
      for (let i = rowIndex; i <= sheet.rowCount; ++i) {
        const row = sheet.getRow(i);
        const flowId = row.getCell(flowIdCol).value as string;
        if (!oldFlowsIds.has(flowId)) {
          return {
            success: false,
            message: 'interface:order_profile.upload_result.flow_mismatch',
          };
        }

        // create new flow
        const thisFlow = oldFlows.find((flow) => flow.id === flowId);
        if (!thisFlow) {
          return {
            success: false,
            message: 'interface:order_profile.upload_result.bad_excel',
          };
        }
        const newFlow: Flow = { ...thisFlow };
        newFlow.name = row.getCell(1).value as string;

        const hourlyOrderCounts: number[] = [];
        for (let col = 4; col < flowIdCol; ++col) {
          const value = row.getCell(col).value as string;
          hourlyOrderCounts.push(value ? parseInt(value) : 0);
        }

        if (!isAtLeast1(hourlyOrderCounts)) {
          return {
            success: false,
            message: 'interface:order_profile.upload_result.bad_excel',
          };
        }

        newFlow.hourlyOrderCounts = hourlyOrderCounts;
        newFlows.push(newFlow);
      }

      const newOrderProfile: OrderProfile = {
        uploadedFileName: filename,
      };

      // update state
      historySvc.batch(() => {
        fddmSvc.updateFlows(newFlows);
        opSvc.setOrderProfile(newOrderProfile);
      });

      return { success: true, message: 'interface:order_profile.upload_result.success' };
    },
    [fddmSvc, getFlows, historySvc, opSvc],
  );

  const removeOrderProfile = useCallback(() => {
    const oldFlows = getFlows();

    const newFlows: Flow[] = oldFlows.map((flow) => {
      const newFlow: Flow = { ...flow };
      newFlow.hourlyOrderCounts = minItemsArrayUtils.mapAtLeast1(
        newFlow.hourlyOrderCounts,
        () => defaultFlowTransportLoadPerHour,
      );

      return newFlow;
    });

    fddmSvc.updateFlows(newFlows);
    opSvc.setOrderProfile(null);
  }, [defaultFlowTransportLoadPerHour, fddmSvc, getFlows, opSvc]);

  return { createExcel, downloadExcel, uploadExcel, removeOrderProfile };
};
